// @flow
import React, { type Node, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toLower } from 'ramda'

import {
  Td,
  TrExpandable,
  useCheckStateContext,
  getHeaderAttrsDefault,
  fancyRowExpandedHeaderClassName,
  fancyRowExpandedContentClassName,
  useFancyRowExpandedStyle,
  FancyRowHeader,
} from 'app/common/ui-components/table'

import Person from 'app/common/classes/person'

import type { OrderRequest } from '../../dataTypes/orderRequests/OrderRequest.types'
import { statuses } from '../../dataTypes/orderRequests/statuses'
import CellStatus from './cells/CellStatus'
import CellWho from './cells/CellWho'
import CellDate from './cells/CellDate'
import CellText from './cells/CellText'
import CellNumber from './cells/CellNumber'
import CellExpiration from './cells/CellExpiration'
import {
  status2ColorMap,
  undefinedStatusColor,
  status2TranslationMap,
  undefinedStatusTranslation,
} from '../statusProps'
import { role2translationMap } from './role2translationMap'
import { foodMode2translarionMap } from './foodMode2translarionMap'
import ProductList from './ProductList'
import ProductListFooter from './ProductListFooter'

import styles from './TableRow.module.scss';

type Props = {
  index: number,
  disabled?: boolean,
  customerId: string,
  data: OrderRequest,
  columns: Array<string>,
  updateRowNode: (el: HTMLTableRowElement | null, i: number) => void,
  calcTopShift: (index: number) => number,
  trElem: HTMLTableRowElement | null,
  customer: Person,
  onUpdate: () => Promise<void> | void,
  editable?: boolean,
}

const TableRow = ({
  index,
  disabled,
  customerId,
  data,
  columns,
  updateRowNode,
  calcTopShift,
  trElem,
  customer,
  onUpdate,
  editable,
}: Props): Node => {
  const { t } = useTranslation('cafeteria')

  const {
    id,
    status,
    date,
    createdBy,
    createdByRole,
    groupTitle,
    groupType,
    foodMode,
    total,
    expiresAt,
    changeId,
    display: {
      putOnFoodOption,
      removeFromFoodOption,
      changeOption,
      exchangeComplexOption,
      expiredMessage,
      expiredUntillMessage,
    },
    putOnFoodOnlySingleComplex,
  } = data

  const [expandedId, setExpandedId] = useCheckStateContext()
  const expanded = expandedId === id
  const expandedNothing = !expandedId
  const expandedOther = !!expandedId && expandedId !== id

  const color = (status && status2ColorMap.get(status)) || undefinedStatusColor
  const statusTitle: string =
    t((status && status2TranslationMap.get(status)) || undefinedStatusTranslation) || ''
  const role: ?string = (createdByRole && t(role2translationMap.get(createdByRole))) || ''
  const foodModeText = (foodMode && t(foodMode2translarionMap.get(foodMode))) || ''

  const cells = [
    <CellStatus id={id} color={color} statusTitle={statusTitle} />,
    <CellDate date={date} />,
    <CellWho name={createdBy ? createdBy.lastFM : ''} role={role} />,
    <CellText text={groupTitle} />,
    <CellText text={foodModeText} />,
    <CellNumber number={total} />,
    <CellExpiration
      expiredMessage={expiredMessage}
      expiredUntillMessage={expiredUntillMessage}
      expiresAt={expiresAt}
    />,
  ]

  const reference = useCallback(
    (el: HTMLTableRowElement | null) => {
      updateRowNode(el, index)
    },
    [index, updateRowNode],
  )

  const fancyStyle = useFancyRowExpandedStyle(color)

  const showFooter =
    editable && (putOnFoodOption || removeFromFoodOption || changeOption || exchangeComplexOption)

  return (
    <TrExpandable
      id={id}
      reference={reference}
      className={styles.root}
      classNameExpanded={fancyRowExpandedHeaderClassName}
      styleExpanded={fancyStyle}
      disabled={disabled}
      expanded={expanded}
      expandedContent={
        <ProductList
          className={fancyRowExpandedContentClassName}
          status={status}
          customerId={customerId}
          orderRequestId={id}
          footerContent={
            date && showFooter ? (
              <ProductListFooter
                putOnFoodOption={putOnFoodOption}
                removeFromFoodOption={removeFromFoodOption}
                changeOption={changeOption}
                exchangeComplexOption={exchangeComplexOption}
                customer={customer}
                date={date}
                groupType={groupType}
                orderRequestId={id}
                changeId={changeId}
                onUpdate={onUpdate}
                foodModeTitle={toLower(foodModeText || '???')}
                putOnFoodOnlySingleComplex={putOnFoodOnlySingleComplex}
              />
            ) : null
          }
        />
      }
      onExpand={setExpandedId}
      columnsAmount={columns.length + 1}
      tinted={expandedOther || (status === statuses.Removed && !expanded)}
      hoverHighlighted={expandedNothing}
      interlineBorder
      alignTop
    >
      {columns.map((columnId, i) => (
        <Td key={columnId} {...getHeaderAttrsDefault(columnId)}>
          {cells[i]}
        </Td>
      ))}

      <FancyRowHeader
        expanded={expanded}
        requestTopShift={calcTopShift}
        rowIndex={index}
        color={color}
        trElem={trElem}
      />
    </TrExpandable>
  )
}

export default TableRow
